import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { flushSync } from 'react-dom';

/** What the scroll wrapper gets while it scrolls sideways (nothing otherwise). */
export interface ScrollRegionProps {
  role?: 'region';
  'aria-label'?: string;
  tabIndex?: number;
}

/**
 * For a wrapper that scrolls sideways when its content is wider than the card. While the content
 * overflows, the wrapper becomes a named, keyboard-focusable region so it can be scrolled without
 * a mouse (axe: scrollable-region-focusable). Otherwise it gets nothing, so it doesn't repeat the
 * card's own landmark name.
 *
 * The region is set in the same task that made the content overflow, before anything else can run
 * (a Tab press, an accessibility check): measured as the wrapper mounts, as soon as the content
 * changes, and as soon as the browser reports a new size. An update scheduled the usual way lands
 * in a later task, and for that moment the wrapper scrolls without being reachable by keyboard.
 * A new window width reaches the page only in the browser's next frame, before it paints; the
 * region follows it there, with the rest of the page.
 */
export function useScrollRegion(
  ref: RefObject<HTMLElement | null>,
  label: string,
): ScrollRegionProps {
  const [scrollable, setScrollable] = useState(false);
  // The wrapper being watched (undefined before the first commit) and how to stop watching it.
  const watched = useRef<{ element: HTMLElement | null; stop: () => void } | undefined>(undefined);

  // After every commit, because a wrapper can mount after the hook does (a table that replaces
  // its loading or empty state) or be replaced. An unchanged wrapper costs one comparison, and
  // the state is set only when the wrapper changes, so it can't loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const element = ref.current;
    if (watched.current?.element === element) return;
    watched.current?.stop();
    watched.current = { element, stop: watch(element, setScrollable) };
  });
  useLayoutEffect(
    () => () => {
      watched.current?.stop();
      watched.current = undefined;
    },
    [],
  );

  return scrollable
    ? { role: 'region', 'aria-label': `${label} (scrolls sideways)`, tabIndex: 0 }
    : {};
}

/**
 * Watches one wrapper and reports whether its content overflows, only when that changes. Returns
 * how to stop. The first answer comes at once (from a layout effect, so it's applied before paint);
 * later ones are applied synchronously with `flushSync`, which is safe in an observer's callback:
 * it never runs while React renders or commits.
 */
function watch(element: HTMLElement | null, report: (scrollable: boolean) => void): () => void {
  // No wrapper on the page (yet), or no ResizeObserver to watch it with (jsdom): no region.
  if (element === null || typeof ResizeObserver === 'undefined') {
    report(false);
    return () => undefined;
  }
  let last = element.scrollWidth > element.clientWidth + 1;
  report(last);
  const measure = () => {
    const next = element.scrollWidth > element.clientWidth + 1;
    if (next === last) return;
    last = next;
    flushSync(() => {
      report(next);
    });
  };
  // The wrapper (the card narrows) and what it holds: the content can grow wider (a web font
  // loads) while the wrapper keeps its size, and only the content's resize says so. The browser
  // reports sizes once per frame, after layout and before paint.
  const sizes = new ResizeObserver(measure);
  const observeSizes = () => {
    sizes.disconnect();
    sizes.observe(element);
    for (const child of Array.from(element.children)) sizes.observe(child);
  };
  observeSizes();
  // Content changes (rows arrive, a table is swapped for its empty state, a value gets longer) are
  // reported in the same task, before the next frame. Only what's inside the wrapper is watched,
  // so the region's own attributes never set it off again. A replaced child gets its size watched
  // in its place.
  const content =
    typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver((records) => {
          if (records.some((r) => r.type === 'childList' && r.target === element)) observeSizes();
          measure();
        });
  content?.observe(element, { childList: true, subtree: true, characterData: true });
  return () => {
    sizes.disconnect();
    content?.disconnect();
  };
}
